/**
 * AI Memory Service — shared Y.Doc read/write layer.
 *
 * Used by:
 * - AI Chat tool executor (read/write memories during conversation)
 * - useAiChat hook (read master for system prompt injection)
 * - Settings panel (via useAiMemoryDoc hook which wraps this)
 *
 * Handles DexieYProvider lifecycle (load/release) with ref-counting.
 */

import * as Y from "yjs"
import { DexieYProvider } from "y-dexie"
import { db } from "@/db/schema"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MemoryEntry {
  label: string
  content: string
  source: "explicit" | "inferred"
  confidence: number
  updatedAt: string
}

export interface AreaInfo {
  key: string
  label: string
  summary: string
  topicCount: number
  updatedAt: string
}

export const MEMORY_AREAS = [
  "scheduling",
  "organization",
  "preferences",
  "routines",
  "people",
  "custom",
] as const

export type MemoryArea = (typeof MEMORY_AREAS)[number]

// ---------------------------------------------------------------------------
// Y.Doc lifecycle — ref-counted singleton
// ---------------------------------------------------------------------------

let _doc: Y.Doc | null = null
/** GC anchor — keeps DexieYProvider alive so pending writes flush. */
let _provider: DexieYProvider | null = null
void _provider // prevent TS6133 (intentionally unused read)
let _refCount = 0
let _loadPromise: Promise<Y.Doc | null> | null = null

/**
 * Acquire the memory Y.Doc. Ref-counted — call releaseMemoryDoc() when done.
 * Multiple callers can hold the same doc simultaneously.
 */
export async function acquireMemoryDoc(): Promise<Y.Doc | null> {
  if (_doc) {
    _refCount++
    return _doc
  }

  // Avoid concurrent loads
  if (_loadPromise) {
    const doc = await _loadPromise
    if (doc) _refCount++
    return doc
  }

  _loadPromise = (async () => {
    try {
      const userId = db.cloud.currentUserId
      if (!userId) return null

      // Ensure the row exists
      let row = await db.ai_memory.get("memory")
      if (!row) {
        await db.ai_memory.put({
          id: "memory",
          owner: userId,
          realmId: userId,
        })
        row = await db.ai_memory.get("memory")
      }
      if (!row) return null

      const yDoc = row.doc as Y.Doc
      if (!yDoc || typeof yDoc.getMap !== "function") return null

      const provider = DexieYProvider.load(yDoc)
      await provider.whenLoaded

      // Initialize structure if empty
      const root = yDoc.getMap("root")
      if (!root.has("master")) {
        yDoc.transact(() => {
          root.set("master", "")
          for (const area of MEMORY_AREAS) {
            const areaMap = new Y.Map()
            areaMap.set("label", area.charAt(0).toUpperCase() + area.slice(1))
            areaMap.set("summary", "")
            areaMap.set("updatedAt", new Date().toISOString())
            areaMap.set("details", new Y.Map())
            root.set(area, areaMap)
          }
        })
      }

      _doc = yDoc
      _provider = provider
      _refCount = 1
      return yDoc
    } catch {
      return null
    } finally {
      _loadPromise = null
    }
  })()

  return _loadPromise
}

/**
 * Release a reference to the memory Y.Doc.
 * The Y.Doc is kept alive as a persistent singleton — releasing only
 * decrements the refCount. DexieYProvider needs to remain connected to
 * flush async IndexedDB writes; destroying it loses pending data.
 */
export function releaseMemoryDoc(): void {
  if (_refCount <= 0) return
  _refCount--
  // Intentionally NOT releasing the provider or nulling _doc.
  // The Y.Doc stays in memory (~20-150KB) so that:
  // 1. Pending IndexedDB writes from DexieYProvider complete
  // 2. Subsequent reads see in-memory state immediately
  // 3. No race between transact() and provider disconnect
}

// ---------------------------------------------------------------------------
// Read operations
// ---------------------------------------------------------------------------

/** Read master text (always-on context). Returns "" if not available. */
export async function readMaster(): Promise<string> {
  const doc = await acquireMemoryDoc()
  if (!doc) return ""
  try {
    return (doc.getMap("root").get("master") as string) || ""
  } finally {
    releaseMemoryDoc()
  }
}

/**
 * Read ALL memory for system prompt injection.
 * Returns master (if set) + all entries formatted as compact bullet points.
 * This is the primary injection function — ensures AI always has full context.
 */
export async function readAllForPrompt(): Promise<string> {
  const doc = await acquireMemoryDoc()
  if (!doc) return ""
  try {
    const root = doc.getMap("root")
    const parts: string[] = []

    // Include master summary if manually written
    const master = (root.get("master") as string) || ""
    if (master) parts.push(master)

    // Collect all entries from all areas
    for (const area of MEMORY_AREAS) {
      const am = root.get(area) as Y.Map<unknown> | undefined
      if (!am) continue
      const details = am.get("details") as Y.Map<string> | undefined
      if (!details || details.size === 0) continue

      const label = (am.get("label") as string) || area
      const entries: string[] = []
      details.forEach((val) => {
        try {
          const entry = JSON.parse(val) as MemoryEntry
          if (entry.content) entries.push(entry.content)
        } catch { /* skip malformed */ }
      })
      if (entries.length > 0) {
        parts.push(`[${label}] ${entries.join(". ")}`)
      }
    }

    return parts.join("\n")
  } finally {
    releaseMemoryDoc()
  }
}

/** Read a specific area's summary + all entries. */
export async function readArea(area: string): Promise<{
  label: string
  summary: string
  entries: Record<string, MemoryEntry>
} | null> {
  const doc = await acquireMemoryDoc()
  if (!doc) return null
  try {
    const root = doc.getMap("root")
    const am = root.get(area) as Y.Map<unknown> | undefined
    if (!am) return null

    const details = am.get("details") as Y.Map<string> | undefined
    const entries: Record<string, MemoryEntry> = {}
    details?.forEach((val, key) => {
      try {
        entries[key] = JSON.parse(val)
      } catch { /* skip */ }
    })

    return {
      label: (am.get("label") as string) || area,
      summary: (am.get("summary") as string) || "",
      entries,
    }
  } finally {
    releaseMemoryDoc()
  }
}

/** Read a specific entry from an area. */
export async function readEntry(area: string, key: string): Promise<MemoryEntry | null> {
  const doc = await acquireMemoryDoc()
  if (!doc) return null
  try {
    const root = doc.getMap("root")
    const am = root.get(area) as Y.Map<unknown> | undefined
    if (!am) return null
    const details = am.get("details") as Y.Map<string> | undefined
    const raw = details?.get(key)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  } finally {
    releaseMemoryDoc()
  }
}

/** List all areas with summary info. */
export async function listAreas(): Promise<AreaInfo[]> {
  const doc = await acquireMemoryDoc()
  if (!doc) return []
  try {
    const root = doc.getMap("root")
    const result: AreaInfo[] = []
    for (const area of MEMORY_AREAS) {
      const am = root.get(area) as Y.Map<unknown> | undefined
      if (!am) continue
      const details = am.get("details") as Y.Map<string> | undefined
      result.push({
        key: area,
        label: (am.get("label") as string) || area,
        summary: (am.get("summary") as string) || "",
        topicCount: details?.size ?? 0,
        updatedAt: (am.get("updatedAt") as string) || "",
      })
    }
    return result
  } finally {
    releaseMemoryDoc()
  }
}

// ---------------------------------------------------------------------------
// Write operations
// ---------------------------------------------------------------------------

/** Save a new memory entry. Auto-creates area if needed. */
export async function saveEntry(
  area: MemoryArea,
  key: string,
  entry: MemoryEntry,
): Promise<boolean> {
  const doc = await acquireMemoryDoc()
  if (!doc) return false
  try {
    doc.transact(() => {
      const root = doc.getMap("root")
      let am = root.get(area) as Y.Map<unknown> | undefined
      if (!am) {
        am = new Y.Map()
        am.set("label", area.charAt(0).toUpperCase() + area.slice(1))
        am.set("summary", "")
        am.set("updatedAt", new Date().toISOString())
        am.set("details", new Y.Map())
        root.set(area, am)
      }
      const details = am.get("details") as Y.Map<string>
      details.set(key, JSON.stringify(entry))
      am.set("updatedAt", new Date().toISOString())
    })
    return true
  } catch {
    return false
  } finally {
    releaseMemoryDoc()
  }
}

/** Update an existing memory entry. */
export async function updateEntry(
  area: string,
  key: string,
  content: string,
): Promise<boolean> {
  const doc = await acquireMemoryDoc()
  if (!doc) return false
  try {
    doc.transact(() => {
      const root = doc.getMap("root")
      const am = root.get(area) as Y.Map<unknown> | undefined
      if (!am) return
      const details = am.get("details") as Y.Map<string>
      const existing = details.get(key)
      if (existing) {
        const parsed = JSON.parse(existing) as MemoryEntry
        parsed.content = content
        parsed.updatedAt = new Date().toISOString()
        details.set(key, JSON.stringify(parsed))
      } else {
        // Create new if doesn't exist
        details.set(key, JSON.stringify({
          label: key,
          content,
          source: "explicit",
          confidence: 1.0,
          updatedAt: new Date().toISOString(),
        }))
      }
      am.set("updatedAt", new Date().toISOString())
    })
    return true
  } catch {
    return false
  } finally {
    releaseMemoryDoc()
  }
}

/** Delete a memory entry. */
export async function deleteMemoryEntry(area: string, key: string): Promise<boolean> {
  const doc = await acquireMemoryDoc()
  if (!doc) return false
  try {
    doc.transact(() => {
      const root = doc.getMap("root")
      const am = root.get(area) as Y.Map<unknown> | undefined
      if (!am) return
      const details = am.get("details") as Y.Map<string>
      details.delete(key)
      am.set("updatedAt", new Date().toISOString())
    })
    return true
  } catch {
    return false
  } finally {
    releaseMemoryDoc()
  }
}
